import { useEffect, useMemo, useState, type CSSProperties, type FormEvent } from 'react';
import { browserLocaleContext, createAppTranslator, type TranslationVariables } from '@ai-factory/i18n';

import {
  acceptInvitation,
  completeAuthCallback,
  completePasswordRecovery,
  createAuthClient,
  getInvitation,
  requestPasswordReset,
  sendMagicLink,
  signInWithPassword,
  signOut,
  signUp,
  updatePassword,
  type AuthRuntimeConfig,
  type ActivationResult,
  type InvitationDetails,
} from './index';
import './auth.css';

export type AuthScreen =
  'login' | 'signup' | 'forgot-password' | 'email-verification' | 'invitation' | 'suspended';

export interface AuthBrand {
  name: string;
  eyebrow: string;
  tagline: string;
  background: string;
  surface: string;
  text: string;
  muted: string;
  primary: string;
  primaryText: string;
  accent: string;
  motif: 'grid' | 'interior' | 'studio' | 'network' | 'celestial';
}

export interface AuthPageProps {
  screen: AuthScreen;
  config: AuthRuntimeConfig;
  brand: AuthBrand;
  /**
   * The product's resolved visitor locale. Supplying this is important for
   * marketing applications that intentionally default to English instead of
   * inheriting the operator's browser language.
   */
  locale?: string;
  resolveSuccessPath?: (activation: ActivationResult) => string;
}

function messageFor(
  error: unknown,
  translate: (key: string, variables?: TranslationVariables) => string,
): string {
  const value = error instanceof Error ? error.message : 'AUTH_REQUEST_FAILED';
  if (value.includes('Invalid login credentials')) return translate('error.invalid_credentials');
  if (value.includes('Email not confirmed')) return translate('error.email_unconfirmed');
  if (value.includes('rate')) return translate('error.rate_limited');
  if (value.includes('password_too_short')) return translate('error.password_too_short');
  if (value.includes('password_too_long')) return translate('error.password_too_long');
  if (value.includes('RECOVERY_LINK_INVALID')) return translate('error.recovery_link_invalid');
  return value.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase());
}

function localAuthPath(path: string): string {
  const requestedLocale = new URLSearchParams(window.location.search).get('locale');
  if (!requestedLocale) return path;
  const destination = new URL(path, window.location.origin);
  destination.searchParams.set('locale', requestedLocale);
  return `${destination.pathname}${destination.search}${destination.hash}`;
}

function navigate(path: string): void {
  window.location.assign(localAuthPath(path));
}

export function AuthPage({ screen, config, brand, locale, resolveSuccessPath }: AuthPageProps) {
  const [implicitRecoveryAccessToken] = useState(() => {
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    return fragment.get('type') === 'recovery' ? fragment.get('access_token') : null;
  });
  const client = useMemo(() => createAuthClient(config), [config]);
  const localeContext = useMemo(
    () => browserLocaleContext(locale ? { locale } : {}),
    [locale],
  );
  const authText = useMemo(
    () => createAppTranslator('auth', localeContext.locale),
    [localeContext.locale],
  );
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [accountName, setAccountName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [pending, setPending] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [invitation, setInvitation] = useState<InvitationDetails | null>(null);
  const [recoveryReady, setRecoveryReady] = useState(false);

  const token = new URLSearchParams(window.location.search).get('token');
  const code = new URLSearchParams(window.location.search).get('code');
  const isRecovery = new URLSearchParams(window.location.search).get('mode') === 'recovery'
    || implicitRecoveryAccessToken !== null;
  const restrictionReason = new URLSearchParams(window.location.search).get('reason') ?? 'account_suspended';
  const platformConsoleAccessDenied = config.audience === 'internal'
    && restrictionReason === 'platform_console_invitation_required';
  const style = {
    '--auth-bg': brand.background,
    '--auth-surface': brand.surface,
    '--auth-text': brand.text,
    '--auth-muted': brand.muted,
    '--auth-primary': brand.primary,
    '--auth-primary-text': brand.primaryText,
    '--auth-accent': brand.accent,
  } as CSSProperties;

  useEffect(() => {
    if (screen === 'invitation' && token) {
      setPending(true);
      void getInvitation(config, token)
        .then((value) => setInvitation(value))
        .catch((reason: unknown) => setError(messageFor(reason, authText)))
        .finally(() => setPending(false));
    }
    if (screen === 'email-verification' && isRecovery && !code && !implicitRecoveryAccessToken) {
      setError(authText('error.recovery_link_invalid'));
    }
    if (screen === 'email-verification' && (code || implicitRecoveryAccessToken)) {
      setPending(true);
      void (
        isRecovery
          ? completePasswordRecovery(client, code, implicitRecoveryAccessToken)
          : completeAuthCallback(client, config, code!)
      )
        .then((activation) => {
          if (isRecovery) {
            setRecoveryReady(true);
            setNotice(authText('notice.identity_confirmed'));
          } else if (!activation || !activation.allowed)
            navigate(
              `/suspended?reason=${encodeURIComponent(activation?.reason ?? 'access_denied')}`,
            );
          else {
            const destination = resolveSuccessPath?.(activation);
            if (destination) navigate(destination);
            else setNotice(authText('notice.email_verified'));
          }
        })
        .catch((reason: unknown) => setError(messageFor(reason, authText)))
        .finally(() => setPending(false));
    }
  }, [authText, client, code, config, implicitRecoveryAccessToken, isRecovery, resolveSuccessPath, screen, token]);

  async function run(action: () => Promise<void>): Promise<void> {
    setPending(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (reason) {
      setError(messageFor(reason, authText));
    } finally {
      setPending(false);
    }
  }

  function statusRegion() {
    return (
      <div className="auth-status" aria-live="polite">
        {pending ? <p className="auth-loading">{authText('status.working')}</p> : null}
        {error ? (
          <p className="auth-error" role="alert">
            {error}
          </p>
        ) : null}
        {notice ? <p className="auth-success">{notice}</p> : null}
      </div>
    );
  }

  async function login(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const activation = await signInWithPassword(client, config, email, password, token);
      if (!activation.allowed || activation.account_status === 'suspended')
        navigate(`/suspended?reason=${encodeURIComponent(activation.reason)}`);
      else {
        const destination = resolveSuccessPath?.(activation);
        if (destination) navigate(destination);
        else setNotice(authText('notice.signed_in', { brand: brand.name }));
      }
    });
  }

  async function signup(event: FormEvent) {
    event.preventDefault();
    await run(async () => {
      const result = await signUp(
        client,
        config,
        email,
        password,
        {
          accountName: accountName || email.split('@')[0] || 'Invited member',
          ...(config.audience === 'business' && !token ? { organizationName } : {}),
          locale: localeContext.locale,
          timezone: localeContext.timezone,
        },
        token,
      );
      if (!result.requiresEmailVerification && result.activation) {
        const destination = resolveSuccessPath?.(result.activation);
        if (destination) {
          navigate(destination);
          return;
        }
      }
      setNotice(
        result.requiresEmailVerification
          ? authText('notice.check_inbox')
          : authText('notice.account_created', { brand: brand.name }),
      );
    });
  }

  function field(
    id: string,
    label: string,
    type: string,
    value: string,
    update: (value: string) => void,
    autoComplete: string,
    constraints?: { minLength?: number; maxLength?: number },
  ) {
    return (
      <label className="auth-field" htmlFor={id}>
        <span>{label}</span>
        <input
          id={id}
          type={type}
          value={value}
          onChange={(event) => update(event.target.value)}
          autoComplete={autoComplete}
          minLength={constraints?.minLength}
          maxLength={constraints?.maxLength}
          required
        />
      </label>
    );
  }

  let content;
  if (screen === 'login')
    content = (
      <>
        <header>
          <p className="auth-kicker">{authText('login.kicker')}</p>
          <h1>{authText('login.title')}</h1>
          <p>{authText('login.description', { brand: brand.name })}</p>
        </header>
        <form onSubmit={(event) => void login(event)}>
          {field(
            'email',
            config.audience === 'business' ? authText('field.work_email') : authText('field.email'),
            'email',
            email,
            setEmail,
            'email',
          )}
          {field('password', authText('field.password'), 'password', password, setPassword, 'current-password')}
          <div className="auth-row">
            <label>
              <input type="checkbox" /> {authText('action.remember_me')}
            </label>
            <a href={localAuthPath('/forgot-password')}>{authText('action.forgot_password')}</a>
          </div>
          <button className="auth-primary" disabled={pending}>
            {authText('action.login')}
          </button>
          <div className="auth-divider">
            <span>{authText('action.or')}</span>
          </div>
          <button
            type="button"
            className="auth-secondary"
            disabled={pending || !email}
            onClick={() =>
              void run(async () => {
                await sendMagicLink(client, config, email, localeContext.locale);
                setNotice(authText('notice.magic_link_sent'));
              })
            }
          >
            {authText('action.send_magic_link')}
          </button>
        </form>
        {statusRegion()}
        <p className="auth-switch">
          {authText('copy.no_account')} <a href={localAuthPath('/signup')}>{authText('action.signup')}</a>
        </p>
      </>
    );
  else if (screen === 'signup')
    content =
      config.audience === 'internal' ? (
        <>
          <header>
            <p className="auth-kicker">{authText('signup.internal_kicker')}</p>
            <h1>{authText('signup.title')}</h1>
            <p>{authText('signup.internal_description')}</p>
          </header>
          <a className="auth-button-link" href={localAuthPath('/invitation')}>
            {authText('action.open_invitation')}
          </a>
          {statusRegion()}
          <p className="auth-switch">
            <a href={localAuthPath('/login')}>{authText('action.back_to_login')}</a>
          </p>
        </>
      ) : (
        <>
          <header>
            <p className="auth-kicker">{authText('signup.kicker')}</p>
            <h1>{authText('signup.title')}</h1>
            <p>{authText('signup.description', { brand: brand.name })}</p>
          </header>
          <form onSubmit={(event) => void signup(event)}>
            {config.audience === 'business' && !token
              ? field(
                  'organization',
                  authText('field.organization_name'),
                  'text',
                  organizationName,
                  setOrganizationName,
                  'organization',
                )
              : null}
            {field(
              'account-name',
              config.audience === 'business' ? authText('field.account_name') : authText('field.your_name'),
              'text',
              accountName,
              setAccountName,
              'name',
            )}
            {field(
              'email',
              config.audience === 'business' ? authText('field.work_email') : authText('field.email'),
              'email',
              email,
              setEmail,
              'email',
            )}
            {field(
              'password',
              authText('field.password'),
              'password',
              password,
              setPassword,
              'new-password',
              { minLength: 8, maxLength: 72 },
            )}
            <p className="auth-hint">
              {authText('copy.password_hint')}
            </p>
            <label className="auth-consent">
              <input type="checkbox" required /> {authText('copy.consent_prefix')}{' '}
              <a href="/terms" target="_blank" rel="noreferrer">{authText('copy.terms')}</a>
              {' '}{authText('copy.consent_and')}{' '}
              <a href="/privacy" target="_blank" rel="noreferrer">{authText('copy.privacy')}</a>.
            </label>
            <button className="auth-primary" disabled={pending}>
              {authText('action.create_account')}
            </button>
          </form>
          {statusRegion()}
          <p className="auth-switch">
            {authText('copy.already_registered')} <a href={localAuthPath('/login')}>{authText('action.login')}</a>
          </p>
        </>
      );
  else if (screen === 'forgot-password')
    content = (
      <>
        <header>
          <p className="auth-kicker">{authText('forgot.kicker')}</p>
          <h1>{authText('forgot.title')}</h1>
          <p>{authText('forgot.description')}</p>
        </header>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await requestPasswordReset(client, config, email, localeContext.locale);
              setNotice(authText('notice.password_reset_sent'));
            });
          }}
        >
          {field('email', authText('field.email'), 'email', email, setEmail, 'email')}
          <button className="auth-primary" disabled={pending}>
            {authText('action.send_reset_link')}
          </button>
        </form>
        {statusRegion()}
        <p className="auth-switch">
          <a href={localAuthPath('/login')}>{authText('action.back_to_login')}</a>
        </p>
      </>
    );
  else if (screen === 'email-verification')
    content = (
      <>
        <header>
          <div className="auth-state-icon">✉</div>
          <p className="auth-kicker">{authText('verification.kicker')}</p>
          <h1>{isRecovery ? authText('verification.recovery_title') : authText('verification.title')}</h1>
          <p>
            {isRecovery
              ? authText('verification.recovery_description')
              : authText('verification.description')}
          </p>
        </header>
        {isRecovery && recoveryReady ? (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void run(async () => {
                await updatePassword(client, password);
                setNotice(authText('notice.password_updated'));
              });
            }}
          >
            {field(
              'password',
              authText('field.new_password'),
              'password',
              password,
              setPassword,
              'new-password',
              { minLength: 8, maxLength: 72 },
            )}
            <button className="auth-primary" disabled={pending}>
              {authText('action.update_password')}
            </button>
          </form>
        ) : null}
        {statusRegion()}
        {isRecovery && !recoveryReady && !pending ? (
          <p className="auth-switch">
            <a href={localAuthPath('/forgot-password')}>{authText('action.forgot_password')}</a>
          </p>
        ) : null}
        <button className="auth-secondary" onClick={() => navigate('/login')}>
          {authText('action.back_to_login')}
        </button>
      </>
    );
  else if (screen === 'invitation')
    content = (
      <>
        <header>
          <p className="auth-kicker">{authText('invitation.kicker')}</p>
          <h1>{authText('invitation.title')}</h1>
          <p>{authText('invitation.description')}</p>
        </header>
        {invitation ? (
          <div className="auth-invitation">
            <strong>{invitation.account_name}</strong>
            <span>{invitation.application_code}</span>
            <span>{authText('copy.role', { role: invitation.role })}</span>
            <span>{authText('copy.status', { status: invitation.status })}</span>
          </div>
        ) : null}
        {statusRegion()}
        <button
          className="auth-primary"
          disabled={
            pending ||
            !invitation ||
            invitation.status !== 'pending' ||
            invitation.application_code !== config.applicationCode
          }
          onClick={() =>
            void run(async () => {
              if (!token) throw new Error('invitation_token_missing');
              await acceptInvitation(client, config, token);
              setNotice(authText('notice.invitation_accepted'));
            })
          }
        >
          {authText('action.accept_invitation')}
        </button>
        {invitation && invitation.application_code !== config.applicationCode ? (
          <p className="auth-error">{authText('copy.different_application')}</p>
        ) : null}
        <p className="auth-switch">
          <a href={localAuthPath(`/login?token=${encodeURIComponent(token ?? '')}`)}>{authText('action.login')}</a>{' '}{authText('action.or')}{' '}
          <a href={localAuthPath(`/signup?token=${encodeURIComponent(token ?? '')}`)}>{authText('copy.invited_identity')}</a>
        </p>
      </>
    );
  else
    content = (
      <>
        <header>
          <div className="auth-state-icon auth-danger">!</div>
          <p className="auth-kicker">{platformConsoleAccessDenied ? authText('suspended.platform_admin_kicker') : authText('suspended.kicker')}</p>
          <h1>{platformConsoleAccessDenied ? authText('suspended.platform_admin_title') : authText('suspended.title')}</h1>
          <p>{platformConsoleAccessDenied ? authText('suspended.platform_admin_description') : authText('suspended.description', { brand: brand.name })}</p>
        </header>
        <div className="auth-restricted">
          <strong>{authText('copy.reference')}</strong>
          <span>{restrictionReason}</span>
          <p>{authText('copy.support')}</p>
        </div>
        <button
          className="auth-primary"
          onClick={() => {
            void signOut(client).finally(() => navigate('/login'));
          }}
        >
          {authText('action.logout')}
        </button>
      </>
    );

  return (
    <main
      className={`auth-shell auth-motif-${brand.motif}`}
      style={style}
      data-application-code={config.applicationCode}
    >
      <section className="auth-brand-panel" aria-label={authText('aria.brand', { brand: brand.name })}>
        <div className="auth-brand-mark" aria-hidden="true">
          {brand.name.slice(0, 1)}
        </div>
        <div>
          <p>{brand.eyebrow}</p>
          <h2>{brand.name}</h2>
          <p>{brand.tagline}</p>
        </div>
        <div className="auth-motif" aria-hidden="true" />
      </section>
      <section className="auth-form-panel">
        <div className="auth-mobile-brand">
          <span>{brand.name.slice(0, 1)}</span>
          {brand.name}
        </div>
        <div className="auth-card">{content}</div>
        <footer>{authText('footer.secure')}</footer>
      </section>
    </main>
  );
}
